if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.bindThumbnailListeners();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        
        // Add scroll listener for mobile to sync thumbnails when user swipes/scrolls main gallery
        this.initMobileScrollSync();
      }

      initMobileScrollSync() {
        // Only initialize on mobile
        if (this.mql.matches) return;
        
        // Wait for slider to be available
        let retryCount = 0;
        const maxRetries = 50; // Max 5 seconds wait
        
        const checkSlider = () => {
          if (retryCount >= maxRetries) return;
          
          let scrollableElement = null;
          
          // Try to get the scrollable element
          if (this.elements.viewer) {
            // Try slider property first
            if (this.elements.viewer.slider) {
              scrollableElement = this.elements.viewer.slider;
            } else {
              // Fallback: find the scrollable ul element directly
              scrollableElement = this.elements.viewer.querySelector('.slider, .product__media-list');
            }
          }
          
          if (scrollableElement) {
            // Remove existing listener if any
            if (this.scrollHandler) {
              scrollableElement.removeEventListener('scroll', this.scrollHandler);
            }
            // Add scroll listener
            this.scrollHandler = debounce(this.onGalleryScroll.bind(this), 100);
            scrollableElement.addEventListener('scroll', this.scrollHandler);
          } else {
            // Retry after a short delay if slider not ready
            retryCount++;
            setTimeout(checkSlider, 100);
          }
        };
        
        checkSlider();
      }

      bindThumbnailListeners() {
        if (!this.elements.thumbnails) return;
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          if (button) {
            button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          }
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      onGalleryScroll() {
        // Only handle on mobile
        if (this.mql.matches || !this.elements.viewer || !this.elements.thumbnails) return;
        
        // Get the scrollable element
        const slider = this.elements.viewer.slider || 
                      this.elements.viewer.querySelector('.slider, .product__media-list');
        if (!slider) return;

        // Find the currently visible/centered media item
        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
        if (mediaItems.length === 0) return;

        const sliderRect = slider.getBoundingClientRect();
        const sliderCenter = sliderRect.left + sliderRect.width / 2;
        
        let activeMedia = null;
        let minDistance = Infinity;

        // Find the media item closest to the center of the viewport
        mediaItems.forEach((item) => {
          // Skip hidden items
          if (item.hidden || item.style.display === 'none' || item.classList.contains('hidden')) return;
          
          const itemRect = item.getBoundingClientRect();
          const itemCenter = itemRect.left + itemRect.width / 2;
          const distance = Math.abs(itemCenter - sliderCenter);
          
          // Only consider items that are at least partially visible
          if (itemRect.right > sliderRect.left && itemRect.left < sliderRect.right) {
            if (distance < minDistance) {
              minDistance = distance;
              activeMedia = item;
            }
          }
        });

        // If no item found by center, find the most visible one
        if (!activeMedia) {
          mediaItems.forEach((item) => {
            // Skip hidden items
            if (item.hidden || item.style.display === 'none' || item.classList.contains('hidden')) return;
            
            const itemRect = item.getBoundingClientRect();
            const visibleWidth = Math.min(itemRect.right, sliderRect.right) - Math.max(itemRect.left, sliderRect.left);
            if (visibleWidth > 0) {
              const currentActiveWidth = activeMedia ? 
                Math.min(activeMedia.getBoundingClientRect().right, sliderRect.right) - 
                Math.max(activeMedia.getBoundingClientRect().left, sliderRect.left) : 0;
              if (visibleWidth > currentActiveWidth) {
                activeMedia = item;
              }
            }
          });
        }

        // Fallback to first visible item
        if (!activeMedia) {
          activeMedia = mediaItems.find((item) => {
            if (item.hidden || item.style.display === 'none' || item.classList.contains('hidden')) return false;
            const itemRect = item.getBoundingClientRect();
            return itemRect.right > sliderRect.left && itemRect.left < sliderRect.right;
          });
        }

        if (activeMedia && activeMedia.dataset.mediaId) {
          const mediaId = activeMedia.dataset.mediaId;
          
          // Don't update if already active
          if (activeMedia.classList.contains('is-active')) return;
          
          const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          
          if (thumbnail) {
            // Update active state on media items
            mediaItems.forEach((item) => item.classList.remove('is-active'));
            activeMedia.classList.add('is-active');
            
            // Sync thumbnail
            this.setActiveThumbnail(thumbnail);
          }
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) || this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        const enabledDefault = this.dataset.enabledDefault === 'true';

        if (prepend) {
          // Skip prepend behavior when enabledDefault is true (normal slider behavior)
          if (!enabledDefault) {
            activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

            if (this.elements.thumbnails) {
              const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
             activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
            }

            if (this.elements.viewer.slider) this.elements.viewer.resetPages();
          } else {
            // When enabledDefault is true, still reset pages to ensure slider state is correct
            if (this.elements.viewer && this.elements.viewer.resetPages) {
              this.elements.viewer.resetPages();
            }
          }
          
          // Skip scrollBehavior='auto' when enabledDefault is true
          if (!enabledDefault) {
            // Directly jump to the image without sliding animation when variant is selected
            // Temporarily disable smooth scrolling to ensure instant jump
            const parentElement = activeMedia.parentElement;
            const originalScrollBehavior = parentElement.style.scrollBehavior;
            parentElement.style.scrollBehavior = 'auto';


            // Set scroll position instantly to avoid sliding through previous images
            if (!this.mql.matches || this.elements.thumbnails) {
              // Use requestAnimationFrame to ensure DOM has updated before scrolling
              requestAnimationFrame(() => {
                parentElement.scrollLeft = activeMedia.offsetLeft;
                // Restore original scroll behavior after a brief delay
                setTimeout(() => {
                  parentElement.style.scrollBehavior = originalScrollBehavior || '';
                }, 0);
              });
            } else {
              // Restore immediately if not scrolling
              parentElement.style.scrollBehavior = originalScrollBehavior || '';
            }
          }
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          // Only use scrollTo for non-prepend cases (user clicking thumbnails)
          // Or when enabledDefault is true (normal slider behavior)
          if (!prepend || enabledDefault) {
            if (!this.mql.matches || this.elements.thumbnails) {
              const scrollBehavior = enabledDefault && prepend ? 'smooth' : 'auto';
              activeMedia.parentElement.scrollTo({
                left: activeMedia.offsetLeft,
                behavior: scrollBehavior
              });

              // When enabledDefault is true, ensure slider component updates its state
              // This ensures arrow buttons are enabled/disabled correctly after scrolling
              if (enabledDefault && this.elements.viewer) {
                const updateSlider = () => {
                  if (this.elements.viewer.update) {
                    this.elements.viewer.update();
                  }
                  // Also trigger scroll event to ensure slider component updates
                  const scrollEvent = new Event('scroll', { bubbles: true });
                  if (this.elements.viewer.slider) {
                    this.elements.viewer.slider.dispatchEvent(scrollEvent);
                  }
                };

                if (scrollBehavior === 'smooth') {
                  // Wait for smooth scroll to complete
                  setTimeout(updateSlider, 350);
                } else {
                  // Immediate update for auto scroll
                  requestAnimationFrame(updateSlider);
                }
              }
            }
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // On mobile, do not vertically scroll the page when switching variants/media
          if (!this.mql.matches) return;
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        // Always sync thumbnail with active media (ensures sync even when enabledDefault is true)
        if (!this.elements.thumbnails) return;

        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (activeThumbnail) {
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        // Remove active state from all thumbnails
        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        this.elements.thumbnails
          .querySelectorAll('.thumbnail-list__item, [data-target]')
          .forEach((element) => element.classList.remove('is-active'));

        // Set active state on the selected thumbnail
        thumbnail.classList.add('is-active');
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      // playActiveMedia(activeItem) {
      //   // Skip autoplay once when toggling to videos via the filter buttons
      //   if (window.suppressMediaGalleryAutoplay) {
      //     window.suppressMediaGalleryAutoplay = false;
      //     return;
      //   }
      //   window.pauseAllMedia();
      //   const deferredMedia = activeItem.querySelector('.deferred-media');
      //   if (deferredMedia) deferredMedia.loadContent(false);
      // }
      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        // Videos will not autoplay when made active; user must click play on the video itself.
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
